import React, { useState, useEffect } from 'react';
import Pica from 'pica';
import CloseUp from './CloseUp';
import CloseUpThirdPage from './CloseUpThirdPage';
import Tooltip from './Tooltip';
import configData from '../config.json';

const pica = Pica();

function ArtCatalogForm() {
    const [formData, setFormData] = useState({
        artistName: '',
        birthDate: '',
        deathDate: '',
        artworkTitle: '',
        creationYear: '',
        description: '',
        size: '',
        medium: '',
        signatureDetails: '',
        conservationNotes: '',
        provenance: '',
        location: '',
        exhibition: '',
        literature: '',
        price: '',
        priceLabel: 'Price',
        estimates: '',
        firstPageUpload: null,
        isFirstPageUploadCloseUp: false,
        thirdPageUpload: null,
        isThirdPageUploadCloseUp: false,
        forthPageUpload: null,
        fontStyle: 'AGaramo4',
        fontSize: '12',
        language: 'English'
    });

    const [pdfBase64, setPdfBase64] = useState('');
    const [invalidFields, setInvalidFields] = useState({});
    const [validationError, setValidationError] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [artworks, setArtworks] = useState([]);
    const [key, setKey] = useState(0); // Initial key for file inputs
    const [croppedPageImage, setCroppedPageImage] = useState(null);
    const [croppedThirdPageImage, setCroppedThirdPageImage] = useState(null);
    const [showCloseUp, setShowCloseUp] = useState(false);
    const [showCloseUpThirdPage, setShowCloseUpThirdPage] = useState(false);

    const currentYear = new Date().getFullYear();
    const formRef = React.createRef();

    useEffect(() => {
        if (validationError) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [validationError]);

    useEffect(() => {
        handleSubmitForMultipleArtworks(artworks);
    }, [artworks]);

    const handleCroppedImage = (croppedImageBlob) => {
        resizeAndCompressImage(croppedImageBlob, (compressedFile) => {
            setCroppedPageImage(compressedFile);
        });
    }

    const handleCroppedThirdImage = (croppedImageBlob) => {
        resizeAndCompressImage(croppedImageBlob, (compressedFile) => {
            setCroppedThirdPageImage(compressedFile);
        });
    }

    const handleFileChange = async (e) => {
        const { name, files } = e.target;
        if (!files.length) return;

        const file = files[0];
        resizeAndCompressImage(file, (compressedFile) => {
            setFormData(prevState => ({
                ...prevState,
                [name]: compressedFile,
            }));
        });
    };

    function resizeAndCompressImage(file, callback) {
        // Create an off-screen canvas
        const offScreenCanvas = document.createElement('canvas');
        const img = new Image();
        img.src = URL.createObjectURL(file);

        img.onload = async () => {
            const MAX_WIDTH = 2000; // Maximum width for the compressed image
            const scaleFactor = MAX_WIDTH / img.width;
            offScreenCanvas.width = MAX_WIDTH;
            offScreenCanvas.height = img.height * scaleFactor;

            // Use Pica to resize and compress the image
            await pica.resize(img, offScreenCanvas, {
                unsharpAmount: 80,
                unsharpRadius: 0.6,
                unsharpThreshold: 2,
            });

            // Convert canvas to Blob
            pica.toBlob(offScreenCanvas, file.type, 0.9).then(blob => {
                // Update formData with the compressed image
                const compressedFile = new File([blob], file.name, {
                    type: file.type,
                    lastModified: Date.now(),
                });

                callback(compressedFile);
            });
        };
    }

    const base64PDFToBlobUrl = (pdfBase64) => {
        const binStr = atob(pdfBase64);
        const len = binStr.length;
        const arr = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            arr[i] = binStr.charCodeAt(i);
        }
        const blob = new Blob([arr], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        return url;
    }

    const handleCreatePDF = (e, action) => {
        e.preventDefault();
        if (action === 'preview') {
            if (artworks.length > 0) {
                setShowPreview(true);
                handleSubmitForMultipleArtworks(artworks);
            } else {
                addCroppedImagesToFormData();
                if (!isFormValid()) {
                    setValidationError("Please fill out all required fields.");
                    setShowPreview(false);
                    return;
                } else {
                    setValidationError("");
                    setShowPreview(true);
                    addArtworkThenResetFormData();
                }
            }
        } else {
            setShowPreview(false);
        }
    }

    const handleSubmitForMultipleArtworks = (artworks) => {
        const data = new FormData();

        artworks.forEach((artwork, index) => {
            Object.keys(artwork).forEach(key => {
                if (artwork[key] instanceof File || artwork[key] instanceof Blob) {
                    data.append(`artworks[${index}][${key}]`, artwork[key], artwork[key].name || 'blob');
                } else {
                    data.append(`artworks[${index}][${key}]`, artwork[key]);
                }
            });
        });

        fetch(configData.apiBaseUrl + configData.pdfGenerationUrl, {
            method: 'POST',
            body: data
        })
            .then(response => response.json())
            .then(data => setPdfBase64(data.pdfBase64))
            .catch((error) => console.error('Error:', error));
    };

    const addArtworkThenResetFormData = () => {
        addCroppedImagesToFormData();
        if (!isFormValid()) {
            setValidationError("Please fill out all required fields.");
            return;
        } else {
            setValidationError("");
        }
        setArtworks([...artworks, formData]);
        resetFormData();
    }

    const addCroppedImagesToFormData = () => {
        if (croppedPageImage) {
            formData.firstPageUpload = croppedPageImage;
            formData.isFirstPageUploadCloseUp = true;
        }
        if (croppedThirdPageImage) {
            formData.thirdPageUpload = croppedThirdPageImage;
            formData.isThirdPageUploadCloseUp = true;
        }
    }

    const isFormValid = () => {
        const excludedFields = ['deathDate', 'creationYear', 'thirdPageUpload',
            'description', 'signatureDetails', 'conservationNotes', 'exhibition',
            'literature', 'estimates', 'isFirstPageUploadCloseUp', 'isThirdPageUploadCloseUp'];
        for (const key in formData) {
            if (excludedFields.includes(key)) continue; // Skip excluded fields

            const fieldData = formData[key];
            if (!fieldData) return false; // Field is null or undefined

            if (fieldData.type === 'file' && !fieldData) {
                return false; // File field is empty
            }

            if (fieldData.type === 'string' && (!fieldData || fieldData.trim() === '')) {
                return false; // String field is empty or only contains whitespace
            }
        }
        return true; // All required fields are filled
    };

    const resetFormData = () => {
        setFormData({
            artistName: '',
            birthDate: '',
            deathDate: '',
            artworkTitle: '',
            creationYear: '',
            description: '',
            size: '',
            medium: '',
            signatureDetails: '',
            conservationNotes: '',
            provenance: '',
            location: '',
            exhibition: '',
            literature: '',
            price: '',
            priceLabel: 'Price',
            estimates: '',
            firstPageUpload: null,
            isFirstPageUploadCloseUp: false,
            thirdPageUpload: null,
            isThirdPageUploadCloseUp: false,
            forthPageUpload: null,
            fontStyle: 'AGaramo4',
            fontSize: '12',
            language: 'English'
        });
        setKey(prevKey => prevKey + 1); // Update the key to reset file inputs
        setCroppedPageImage(null);
        setCroppedThirdPageImage(null);
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        validateField(name, value);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'price') {
            const formattedValue = value.replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            setFormData(prevState => ({
                ...prevState,
                price: formattedValue
            }));
        } else if (name === 'estimates') {
            const formattedValue = value.replace(/[^\d-]/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            setFormData(prevState => ({
                ...prevState,
                estimates: formattedValue
            }));
        } else {
            setFormData(prevState => ({
                ...prevState,
                [name]: value
            }));
        }

        validateField(name, value);
    };

    const validateField = (name, value) => {
        const excludedFields = ['deathDate', 'creationYear', 'thirdPageUpload',
            'description', 'signatureDetails', 'conservationNotes', 'exhibition',
            'literature', 'estimates', 'isFirstPageUploadCloseUp', 'isThirdPageUploadCloseUp'];
        if (!value && !excludedFields.includes(name)) {
            setInvalidFields(prev => ({ ...prev, [name]: true }));
        } else {
            setInvalidFields(prev => ({ ...prev, [name]: false }));
        }
    };

    return (
        <div className="container">
            <div className="row">
                {/* Form Column */}
                <div className={showPreview ? "col-md-6" : "col-12"}>
                    {validationError && (
                        <div className="alert alert-danger" role="alert">
                            {validationError}
                        </div>
                    )}
                    <form ref={formRef}>
                        <h4>Artwork</h4>
                        <div className="input-group mb-3">
                            <input type="text"
                                className={`form-control ${invalidFields.artistName ? 'is-invalid' : ''}`}
                                name="artistName"
                                value={formData.artistName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Artist's name" />
                            <input type="number"
                                min="1000"
                                max={currentYear}
                                className={`form-control ${invalidFields.birthDate ? 'is-invalid' : ''}`}
                                name="birthDate"
                                value={formData.birthDate}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Birth year" />
                            <input type="number"
                                min="1000"
                                max={currentYear}
                                className="form-control"
                                name="deathDate"
                                value={formData.deathDate}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Death year" />
                        </div>
                        <div className="input-group mb-3">
                            <input type="text"
                                className={`form-control ${invalidFields.artworkTitle ? 'is-invalid' : ''}`}
                                name="artworkTitle"
                                value={formData.artworkTitle}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Artwork title" />
                            <input type="text"
                                className="form-control"
                                name="creationYear"
                                value={formData.creationYear}
                                onChange={handleChange}
                                placeholder="Creation year" />
                        </div>
                        <div className="input-group mb-3">
                            <textarea
                                className="form-control"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Description - not providing a description will exclude the respective page from the pdf" />
                        </div>
                        <div className="input-group mb-3">
                            <input type="text"
                                className={`form-control ${invalidFields.size ? 'is-invalid' : ''}`}
                                name="size"
                                value={formData.size}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Size (H cm x W cm)" />
                            <input type="text"
                                className={`form-control ${invalidFields.medium ? 'is-invalid' : ''}`}
                                name="medium"
                                value={formData.medium}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Medium" />
                        </div>
                        <div className="input-group mb-3">
                            <input type="text"
                                className="form-control"
                                name="signatureDetails"
                                value={formData.signatureDetails}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Signature details - not providing signature details data will exclude the section from the pdf" />
                        </div>
                        <div className="input-group mb-3">
                            <input type="text"
                                className="form-control"
                                name="conservationNotes"
                                value={formData.conservationNotes}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Conservation notes - not providing conservation notes data will exclude the section from the pdf" />
                        </div>
                        <div className="input-group mb-3">
                            <textarea
                                className={`form-control ${invalidFields.provenance ? 'is-invalid' : ''}`}
                                name="provenance"
                                value={formData.provenance}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Provenance" />
                        </div>
                        <div className="input-group mb-3">
                            <input type="text"
                                className={`form-control ${invalidFields.location ? 'is-invalid' : ''}`}
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Location" />
                        </div>

                        <div className="input-group mb-3">
                            <textarea
                                className="form-control"
                                name="exhibition"
                                value={formData.exhibition}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Exhibition - not providing exhibition data will exclude the section from the pdf" />
                        </div>
                        <div className="input-group mb-3">
                            <textarea
                                className="form-control"
                                name="literature"
                                value={formData.literature}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Literature - not providing literature data will exclude the section from the pdf" />
                        </div>

                        <h4>Pricing</h4>
                        <div className="input-group mb-3">
                            <select className="form-select"
                                id="priceLabel"
                                name="priceLabel"
                                value={formData.priceLabel}
                                onChange={handleChange}>
                                <option value="Price">Price</option>
                                <option value="Starting price">Starting price</option>
                            </select>
                            <input type="text"
                                className={`form-control ${invalidFields.price ? 'is-invalid' : ''}`}
                                name="price"
                                value={formData.price}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder={formData.priceLabel} />
                            <span className="input-group-text">EUR</span>
                        </div>

                        <div className="input-group mb-3">
                            <input type="text"
                                className="form-control"
                                name="estimates"
                                value={formData.estimates}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Estimates - not providing estimates data will exclude the section from the pdf" />
                        </div>

                        <h4>Fonts and Customization</h4>
                        <div className="form-group input-group mb-3">
                            <span className="input-group-text">Font Style</span>
                            <select className="form-select"
                                id="fontStyle"
                                name="fontStyle"
                                value={formData.fontStyle}
                                onChange={handleChange}>
                                <option value="AGaramo4">AGaramo4</option>
                                <option value="AGaramondPro-Bold">AGaramondPro-Bold</option>
                                <option value="AGaramondPro-Bold 2">AGaramondPro-Bold 2</option>
                                <option value="AGaramondPro-BoldItalic">AGaramondPro-BoldItalic</option>
                                <option value="AGaramondPro-BoldItalic 2">AGaramondPro-BoldItalic 2</option>
                                <option value="AGaramondPro-Italic">AGaramondPro-Italic</option>
                                <option value="AGaramondPro-Italic 2">AGaramondPro-Italic 2</option>
                                <option value="AGaramondPro-Italic 3">AGaramondPro-Italic 3</option>
                                <option value="AGaramondPro-Regular">AGaramondPro-Regular</option>
                                <option value="AGaramondPro-Regular 2">AGaramondPro-Regular 2</option>
                                <option value="AGaramondPro-Regular 3">AGaramondPro-Regular 3</option>
                                <option value="Brandon Grotesque Black">Brandon Grotesque Black</option>
                                <option value="Brandon Grotesque Bold Italic">Brandon Grotesque Bold Italic</option>
                                <option value="Brandon Grotesque Light">Brandon Grotesque Light</option>
                                <option value="Brandon Grotesque Light Italic">Brandon Grotesque Light Italic</option>
                                <option value="Brandon Grotesque Medium Italic">Brandon Grotesque Medium Italic</option>
                                <option value="Brandon Grotesque Regular Italic">Brandon Grotesque Regular Italic</option>
                                <option value="Brandon Grotesque Thin Italic">Brandon Grotesque Thin Italic</option>
                            </select>
                        </div>

                        <div className="form-group input-group mb-3">
                            <span className="input-group-text">Font Size</span>
                            <select className="form-select"
                                id="fontSize"
                                name="fontSize"
                                value={formData.fontSize}
                                onChange={handleChange}>
                                <option value="8">8</option>
                                <option value="9">9</option>
                                <option value="10">10</option>
                                <option value="11">11</option>
                                <option value="12">12</option>
                                <option value="14">14</option>
                                <option value="16">16</option>
                                <option value="18">18</option>
                                <option value="20">20</option>
                                <option value="22">22</option>
                                <option value="24">24</option>
                                <option value="26">26</option>
                                <option value="28">28</option>
                                <option value="36">36</option>
                                <option value="48">48</option>
                                <option value="72">72</option>
                            </select>
                        </div>
                        <div className="form-group input-group mb-3">
                            <span className="input-group-text">Language</span>
                            <select className="form-select"
                                id="language"
                                name="language"
                                value={formData.language}
                                onChange={handleChange}>
                                <option value="English">English</option>
                                <option value="Romanian">Romanian</option>
                            </select>
                        </div>

                        <h4>Images</h4>
                        <div className="d-flex align-items-center" >
                            <label className="col-form-label col-3">First Page Image</label>
                            <div className="col-7">
                                {showCloseUp ? (
                                    <CloseUp key={key} setCroppedImage={handleCroppedImage} />
                                ) : (
                                    <input className="form-control"
                                        key={key}
                                        type="file"
                                        name="firstPageUpload"
                                        accept="image/*"
                                        onChange={handleFileChange} />
                                )}
                            </div>
                            <div className="col-2">
                                <input type="checkbox" checked={showCloseUp} onChange={() => setShowCloseUp(!showCloseUp)} /> CloseUp
                            </div>
                        </div>
                        <div className="d-flex align-items-center">
                            <label className="col-form-label col-3">Third Page Image <Tooltip /></label>
                            <div className="col-7">
                                {showCloseUpThirdPage ? (
                                    <CloseUpThirdPage key={key} setCroppedImage={handleCroppedThirdImage} />
                                ) : (
                                    <input className="form-control"
                                        key={key}
                                        type="file"
                                        name="thirdPageUpload"
                                        accept="image/*"
                                        onChange={handleFileChange} />
                                )}
                            </div>
                            <div className="col-2">
                                <input type="checkbox" checked={showCloseUpThirdPage} onChange={() => setShowCloseUpThirdPage(!showCloseUpThirdPage)} /> CloseUp
                            </div>
                        </div>
                        <div className="d-flex align-items-center">
                            <label className="col-form-label col-3">Fourth Page Image</label>
                            <div className="col-7">
                                <input className="form-control"
                                    key={key}
                                    type="file"
                                    name="forthPageUpload"
                                    accept="image/*"
                                    onChange={handleFileChange} />
                            </div>
                        </div>

                        <div>
                            <div style={{ display: 'flex' }}>
                                <div
                                    style={{ width: "25%" }}>
                                    <button className="btn btn-light"
                                        type="button"
                                        style={{ width: "90%" }}
                                        onClick={() => window.location.reload()}>New</button>
                                </div>
                                <div
                                    style={{ width: "25%" }}>
                                    <button className="btn btn-light"
                                        type="submit"
                                        style={{ width: "90%" }}
                                        onClick={(e) => handleCreatePDF(e, 'preview')}>Create PDF</button>
                                </div>
                                <div
                                    style={{ width: "25%" }}>
                                    <button className="btn btn-light"
                                        type="button"
                                        style={{ width: "90%" }}
                                        onClick={addArtworkThenResetFormData}>Add another Artwork</button>
                                </div>
                                <div
                                    style={{ width: "25%" }}>
                                    <button className="btn btn-light"
                                        type="button"
                                        style={{ width: "90%" }}
                                        onClick={resetFormData}>Reset fields</button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>


                {/* Preview Column */}
                {showPreview && (
                    <div className="col-md-6">
                        {pdfBase64 && (
                            <iframe
                                title="PDF Preview"
                                src={base64PDFToBlobUrl(pdfBase64)}
                                style={{ width: '100%', height: '99%', padding: '30px' }}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ArtCatalogForm;
